/**
 * Workspace settings > General (DEC-198): the shared Anthropic API key and the GitHub
 * connection. Moved as-is from the single Settings page (§R.8, DEC-086); the GitHub
 * sign-in now returns here, and reads its `?github_connected` / `?github_error` banner
 * here (DEC-090/096).
 */
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Github, KeyRound } from 'lucide-react'
import { settingsApi } from '../../api/settings'
import { githubApi, githubConnectErrorMessage, githubErrorMessage, initiateGithubConnect } from '../../api/github'
import { useAuth } from '../../hooks/useAuth'
import { Button } from '../shared/Button'
import type { SettingsResponse } from '../../types/api'
import { SETTINGS_PATH } from './settingsSections'
import { InstallGitHubApp } from './InstallGitHubApp'
import { ApiKeysCard } from './ApiKeysCard'

export function GeneralSection({ settings }: { settings: SettingsResponse }) {
  const { claims } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [keyInput, setKeyInput] = useState('')
  const [editingKey, setEditingKey] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connectingGithub, setConnectingGithub] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [githubBanner, setGithubBanner] = useState<{ ok: true } | { ok: false; code: string } | null>(null)

  useEffect(() => {
    const connected = searchParams.get('github_connected')
    const errorCode = searchParams.get('github_error')
    if (!connected && !errorCode) return

    if (connected) {
      setGithubBanner({ ok: true })
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    } else if (errorCode) {
      setGithubBanner({ ok: false, code: errorCode })
    }

    const next = new URLSearchParams(searchParams)
    next.delete('github_connected')
    next.delete('github_error')
    setSearchParams(next, { replace: true })
    // A one-time landing signal, not a reactive binding to searchParams (which we clear).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleConnectGithub() {
    const tenant_id = claims?.tenant_id
    if (!tenant_id) return
    setConnectingGithub(true)
    setConnectError(null)
    try {
      const authorizeUrl = await initiateGithubConnect(tenant_id, SETTINGS_PATH.general)
      window.location.href = authorizeUrl
    } catch (err: unknown) {
      setConnectError(githubConnectErrorMessage(err))
      setConnectingGithub(false)
    }
  }

  const setKeyMutation = useMutation({
    mutationFn: (apiKey: string) => settingsApi.setAnthropicKey(apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setEditingKey(false)
      setKeyInput('')
      setError(null)
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to save key')
    },
  })

  const clearKeyMutation = useMutation({
    mutationFn: () => settingsApi.clearAnthropicKey(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  const disconnectGithubMutation = useMutation({
    mutationFn: () => githubApi.disconnect(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  function handleSaveKey(e: React.FormEvent) {
    e.preventDefault()
    if (!keyInput.trim()) return
    setKeyMutation.mutate(keyInput.trim())
  }

  return (
    <>
      {githubBanner && (
        <div
          className={`mb-4 flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
            githubBanner.ok ? 'border-success/30 bg-chip-success' : 'border-critical/30 bg-chip-critical'
          }`}
        >
          <p className={`text-[13px] ${githubBanner.ok ? 'text-success' : 'text-critical'}`}>
            {githubBanner.ok ? 'GitHub connected.' : githubErrorMessage(githubBanner.code)}
          </p>
          <button
            type="button"
            onClick={() => setGithubBanner(null)}
            className="text-[12px] text-faint hover:text-body flex-none"
          >
            Dismiss
          </button>
        </div>
      )}

      {settings.data_deletion_due_at && (
        <div
          className="mb-4 rounded-md border border-critical/30 bg-chip-critical px-3 py-2"
          role="status"
          data-testid="deletion-banner"
        >
          <p className="text-[13px] text-critical m-0">
            The TDIA plan for this workspace was cancelled on GitHub. Scans are stopped, and all of
            this workspace's data will be deleted on{' '}
            {new Date(settings.data_deletion_due_at).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            .
          </p>
        </div>
      )}

      {/* DEC-204 / DEC-203: the workspace's plan and today's scans. Read-only: only
          Athenaworks changes the limit. */}
      <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4" data-testid="plan-card">
        <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
          Plan
        </div>
        <div className="px-4 py-[14px] flex flex-wrap items-center gap-x-8 gap-y-2">
          <div>
            <div className="text-[11px] text-muted">Plan</div>
            <div className="font-semibold text-[13px] text-ink">
              {(settings.plan ?? 'free') === 'free' ? 'Free' : settings.plan}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted">Scans today</div>
            <div className="font-semibold text-[13px] text-ink tabular-nums" data-testid="scans-today">
              {settings.daily_scan_limit === null || settings.daily_scan_limit === undefined
                ? `${settings.scans_today ?? 0}, no daily limit`
                : `${settings.scans_today ?? 0} of ${settings.daily_scan_limit}`}
            </div>
          </div>
          <p className="basis-full text-[11px] text-muted m-0">
            The count resets at 00:00 UTC. Contact Athenaworks to change your limits.
          </p>
        </div>
      </div>

      {/* Anthropic API key */}
      <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
          Anthropic API key
        </div>
        <div className="px-4 py-[14px]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-ground border border-hairline flex items-center justify-center flex-none">
              <KeyRound className="w-4 h-4 text-accent-text" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13px] leading-tight">
                {settings.anthropic_key_configured ? 'Key configured' : 'No key configured'}
              </div>
              <div
                className={`text-[11px] leading-tight text-muted mt-[2px] ${
                  settings.anthropic_key_masked ? 'tabular-nums' : ''
                }`}
              >
                {settings.anthropic_key_configured
                  ? settings.anthropic_key_masked ?? settings.anthropic_key_preview_notice
                  : 'Scans run static-only until a key is set.'}
              </div>
            </div>
          </div>

          {editingKey ? (
            <form onSubmit={handleSaveKey} className="flex items-center gap-2">
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="sk-ant-..."
                autoFocus
                aria-label="Anthropic API key"
                className="flex-1 border border-control rounded-md px-3 py-2 text-[13px] tabular-nums focus:outline-none focus-visible:shadow-focus"
              />
              <Button type="submit" size="sm" loading={setKeyMutation.isPending} disabled={!keyInput.trim()}>
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditingKey(false)
                  setKeyInput('')
                  setError(null)
                }}
              >
                Cancel
              </Button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setEditingKey(true)}>
                {settings.anthropic_key_configured ? 'Replace key' : 'Add key'}
              </Button>
              {settings.anthropic_key_configured && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={clearKeyMutation.isPending}
                  onClick={() => clearKeyMutation.mutate()}
                >
                  Remove
                </Button>
              )}
            </div>
          )}
          {error && <p className="text-[12px] text-critical mt-2">{error}</p>}
        </div>
      </div>

      {/* GitHub connection */}
      <div className="bg-panel border border-hairline rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
          GitHub connection
        </div>
        <div className="px-4 py-[14px]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-ground border border-hairline flex items-center justify-center flex-none">
              <Github className="w-4 h-4 text-accent-text" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13px] leading-tight">
                {settings.github_connected ? 'Connected' : 'Not connected'}
              </div>
              <div className="text-[11px] leading-tight text-muted mt-[2px]">
                {settings.github_connected
                  ? 'Any workspace member can browse and pick a repo from the New scan page.'
                  : 'Any workspace member can connect GitHub from the New scan page, not only admins.'}
              </div>
            </div>
            {settings.github_connected ? (
              <Button
                size="sm"
                variant="ghost"
                loading={disconnectGithubMutation.isPending}
                onClick={() => disconnectGithubMutation.mutate()}
              >
                Disconnect
              </Button>
            ) : (
              <Button size="sm" variant="secondary" loading={connectingGithub} onClick={handleConnectGithub}>
                Connect GitHub
              </Button>
            )}
          </div>
          {connectError && <p className="text-[12px] text-critical mt-2">{connectError}</p>}
          <InstallGitHubApp className="mt-3 pt-3 border-t border-fill-subtle" />
        </div>
      </div>

      <ApiKeysCard />
    </>
  )
}
